import { useCallback, useEffect, useState } from "react"
import { getBmdSkillbuilderPicker } from "../services/bmd-skillbuilder.service"
import type { BmdSkillbuilderPickerResponse } from "../types/bmd-skillbuilder.types"

export type UseBmdSkillbuilderPickerState = {
  picker: BmdSkillbuilderPickerResponse | null
  loading: boolean
  error: string | null
  /** Re-reads the list, for after an assignment changes a row's status. */
  refresh: () => void
}

/**
 * The Skill Builders owed for the selected diagnostic.
 *
 * Re-fetches whenever the selection changes. The in-flight request is aborted,
 * so a slow response for a diagnostic the tutor has moved on from cannot
 * overwrite the current one.
 */
export function useBmdSkillbuilderPicker(
  diagnosticsSessionKey: string,
  studentKey: string
): UseBmdSkillbuilderPickerState {
  const [picker, setPicker] = useState<BmdSkillbuilderPickerResponse | null>(
    null
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState<number>(0)

  const refresh = useCallback(() => setReloadToken(n => n + 1), [])

  useEffect(() => {
    // An empty session key means "all sessions", which the server now accepts.
    // Only a missing STUDENT is a reason not to ask.
    if (!studentKey) {
      setPicker(null)
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    getBmdSkillbuilderPicker(
      {
        diagnostics_session_key: diagnosticsSessionKey || null,
        student_key: studentKey,
      },
      controller.signal
    )
      .then(res => {
        setPicker(res)
        setLoading(false)
      })
      .catch(err => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load Skill Builders")
        setPicker(null)
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [diagnosticsSessionKey, studentKey, reloadToken])

  return { picker, loading, error, refresh }
}
